// Scenario picker for a saved Automation Solution Set. Applying a set copies
// its automations and deployment timing onto the scenario; the Automation
// Impact tab then fine-tunes timing for this scenario only.

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Sparkles } from 'lucide-react';
import {
  automationSolutionProfilePatch,
  clearedAutomationSolutionPatch,
  resolveAutomationDeployment,
  scenarioAutomations,
} from '@rc/engine';
import type { AutomationSolution, ClientProfile } from '@rc/engine/types';
import { api } from '../../lib/api';
import { withGeoclientQuery } from '../../lib/geoclientContext';
import { toast } from '../Toast';

/** Saved sets for a client, plus apply / clear actions that patch the profile. */
export function useAutomationSolutionSets(
  clientId: string | undefined,
  onChange: ((patch: Partial<ClientProfile>) => void) | undefined,
) {
  const [sets, setSets] = useState<AutomationSolution[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!clientId) {
      setSets([]);
      return;
    }
    let cancelled = false;
    api
      .listAutomationSolutions(clientId)
      .then((list) => {
        if (!cancelled) setSets(list || []);
      })
      .catch(() => {
        if (!cancelled) setSets([]);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  const apply = useCallback(
    async (id: string) => {
      if (!clientId || !id || !onChange) return;
      setBusy(true);
      try {
        const full = await api.getAutomationSolution(clientId, id);
        onChange(automationSolutionProfilePatch(full));
        toast('success', `Applied "${full.name}"`);
      } catch (err) {
        toast('error', (err as Error).message);
      } finally {
        setBusy(false);
      }
    },
    [clientId, onChange],
  );

  const clear = useCallback(() => {
    onChange?.(clearedAutomationSolutionPatch());
  }, [onChange]);

  return { sets, busy, apply, clear };
}

const REMOVE_SET = '__remove__';

export default function AutomationSolutionSetCard({
  profile,
  onChange,
  clientId,
  geoclientId,
  modelId,
}: {
  profile: ClientProfile;
  onChange: (patch: Partial<ClientProfile>) => void;
  clientId?: string;
  geoclientId?: string;
  modelId: string;
}) {
  const { sets, busy, apply, clear } = useAutomationSolutionSets(clientId, onChange);
  const appliedId = profile.currentAutomationSolutionId;
  const applied = appliedId ? sets.find((s) => s.id === appliedId) : undefined;
  const automations = scenarioAutomations(profile);
  const enabledCount = automations.filter(
    (a) => resolveAutomationDeployment(a, profile.automationDeployments?.[a.id]).enabled,
  ).length;
  const setChangedSinceApplied =
    !!applied &&
    !!profile.automations &&
    !!profile.currentAutomationSolutionUpdatedAt &&
    applied.updatedAt !== profile.currentAutomationSolutionUpdatedAt;

  const options = sets.map((s) => (
    <option key={s.id} value={s.id}>
      {s.name}
      {s.sample ? ' · sample' : ''}
      {s.modelId && s.modelId !== modelId ? ' · other model' : ''}
    </option>
  ));
  const onSelect = (v: string) => {
    if (v === REMOVE_SET) clear();
    else if (v) void apply(v);
  };

  return (
    <div className="card">
      <div className="card-body space-y-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
            <Sparkles size={14} className="text-brand-purple-500" />
            Automation solution set
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            The automations this scenario deploys. Selecting a set fills the Automation Impact
            tab and the Summary; start and ramp can then be adjusted per scenario.
          </p>
        </div>

        {automations.length > 0 ? (
          <div className="rounded-lg border border-slate-200 px-4 py-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-brand-purple-50 text-brand-purple-700 flex items-center justify-center shrink-0">
              <Sparkles size={16} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-slate-800 truncate">
                {applied?.name || (appliedId ? 'Applied set (deleted)' : 'Copied automations')}
              </div>
              <div className="text-xs text-slate-500">
                <span className="tabular-nums font-medium text-slate-700">{enabledCount}</span> of{' '}
                <span className="tabular-nums">{automations.length}</span> automations enabled
                {applied?.source?.system && <> · from {applied.source.system}</>}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <select
                className="input text-xs max-w-[200px]"
                value={applied ? applied.id : ''}
                disabled={busy || sets.length === 0}
                onChange={(e) => onSelect(e.target.value)}
                title="Switch to another set, or remove automations from this scenario"
              >
                {!applied && (
                  <option value="" disabled>
                    Choose a set…
                  </option>
                )}
                {options}
                <option value={REMOVE_SET}>Remove automation set</option>
              </select>
              {applied && clientId && (
                <Link
                  to={withGeoclientQuery(`/clients/${clientId}/automation-solutions/${applied.id}`, geoclientId)}
                  className="btn-secondary shrink-0"
                >
                  <ExternalLink size={14} /> View
                </Link>
              )}
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-slate-300 px-4 py-6 text-center space-y-3">
            <Sparkles size={20} className="text-slate-400 mx-auto" />
            <div>
              <div className="text-sm font-medium text-slate-700">Select an automation solution set</div>
              <div className="text-xs text-slate-500 mt-0.5">
                {sets.length === 0
                  ? 'No sets have been received for this client yet. They appear here once the automation application sends them.'
                  : 'Until a set is selected, this scenario shows no automation savings.'}
              </div>
            </div>
            <select
              className="input text-sm max-w-sm mx-auto"
              value=""
              disabled={busy || !clientId || sets.length === 0}
              onChange={(e) => onSelect(e.target.value)}
            >
              <option value="">{sets.length === 0 ? 'No saved sets yet' : 'Choose a saved set…'}</option>
              {options}
            </select>
          </div>
        )}

        {setChangedSinceApplied && applied && (
          <div className="flex items-center justify-between gap-2 rounded-md bg-amber-50 ring-1 ring-amber-200 px-3 py-2 text-[11px] text-amber-900">
            <span>
              This set was updated after it was applied. Re-apply to pick up the changes (this
              replaces any timing edits made in this scenario).
            </span>
            <button
              type="button"
              className="font-medium hover:underline shrink-0"
              disabled={busy}
              onClick={() => void apply(applied.id)}
            >
              Re-apply
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
